import { useState, useCallback, useRef, useEffect } from 'react';
import { reelService } from 'api/services/ReelService';
import { useReelsFeedContext } from 'contexts/ReelsFeedContext';
import type { ReelViewModel } from 'api/types/reel';

const FEED_PAGE_SIZE = 10;

function toCleanString(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function normalizeReelItem(raw: unknown): ReelViewModel {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    id: toCleanString(o.id ?? o.Id ?? ''),
    videoUrl: toCleanString(o.videoUrl ?? o.VideoUrl ?? ''),
    userId: toCleanString(o.userId ?? o.UserId ?? ''),
    createdAt: (() => {
      const v = o.createdAt ?? o.CreatedAt ?? o.createdAtUtc ?? o.CreatedAtUtc;
      return typeof v === 'string' ? v : null;
    })(),
    listingId: (() => {
      const v = o.listingId ?? o.ListingId;
      return v == null ? null : String(v);
    })(),
    isSaved: Boolean(o.isSaved ?? o.IsSaved),
  };
}

function extractFeedPayload(rawResult: unknown): {
  items: ReelViewModel[];
  nextCursorUtc: string | null;
  hasMore: boolean | null;
} {
  const resultObj =
    rawResult && typeof rawResult === 'object' ? (rawResult as Record<string, unknown>) : null;

  const candidateSources: unknown[] = [
    resultObj?.data,
    resultObj?.Data,
    (resultObj?.data as Record<string, unknown> | undefined)?.data,
    (resultObj?.data as Record<string, unknown> | undefined)?.Data,
    (resultObj?.Data as Record<string, unknown> | undefined)?.data,
    (resultObj?.Data as Record<string, unknown> | undefined)?.Data,
  ];

  for (const source of candidateSources) {
    if (!source) continue;
    if (Array.isArray(source)) {
      const items = source.map(normalizeReelItem).filter((item) => item.id && item.videoUrl);
      return { items, nextCursorUtc: null, hasMore: null };
    }

    if (typeof source !== 'object') continue;
    const obj = source as Record<string, unknown>;
    const rawItems = obj.items ?? obj.Items;
    if (!Array.isArray(rawItems)) continue;

    const items = rawItems.map(normalizeReelItem).filter((item) => item.id && item.videoUrl);
    const nextCursorUtc =
      (obj.nextCursorUtc as string | null | undefined) ??
      (obj.NextCursorUtc as string | null | undefined) ??
      null;
    const hasMore =
      typeof obj.hasMore === 'boolean'
        ? obj.hasMore
        : typeof obj.HasMore === 'boolean'
          ? (obj.HasMore as boolean)
          : null;

    return { items, nextCursorUtc, hasMore };
  }

  return { items: [], nextCursorUtc: null, hasMore: null };
}

function mergeCreatedReel(
  createdReel: ReelViewModel,
  feedItems: ReelViewModel[]
): ReelViewModel {
  const fromFeed = feedItems.find((item) => item.id === createdReel.id);
  if (fromFeed?.videoUrl) return fromFeed;
  return createdReel;
}

export interface UseReelsFeedResult {
  reels: ReelViewModel[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refetch: (createdReel?: ReelViewModel) => Promise<void>;
  prependCreatedReel: (createdReel: ReelViewModel) => void;
  removeReel: (reelId: string) => void;
  updateReelSaved: (reelId: string, isSaved: boolean) => void;
  addSeenId: (id: string) => void;
}

/**
 * Cursor-based reels feed. Maintains seenIds (max 50) and passes excludeIds to avoid duplicates.
 */
export function useReelsFeed(): UseReelsFeedResult {
  const { getExcludeIds, addSeenId } = useReelsFeedContext();
  const [reels, setReels] = useState<ReelViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const reelsRef = useRef<ReelViewModel[]>([]);
  const nextCursorRef = useRef<string | null>(null);
  const loadingRef = useRef(false);
  const latestRefreshRequestIdRef = useRef(0);

  useEffect(() => {
    reelsRef.current = reels;
  }, [reels]);

  const getSafeExcludeIds = useCallback((forLoadMore: boolean) => {
    const ids = new Set<string>();
    reelsRef.current.forEach((reel) => {
      if (reel.id) ids.add(reel.id);
    });
    if (forLoadMore) {
      getExcludeIds().forEach((id) => {
        if (id) ids.add(id);
      });
    }
    return Array.from(ids);
  }, [getExcludeIds]);

  const fetchPage = useCallback(
    async (
      isLoadMore: boolean,
      options?: { silent?: boolean }
    ): Promise<ReelViewModel[] | null> => {
      if (isLoadMore && loadingRef.current) return null;

      const requestId = isLoadMore ? latestRefreshRequestIdRef.current : latestRefreshRequestIdRef.current + 1;
      if (!isLoadMore) {
        latestRefreshRequestIdRef.current = requestId;
      }

      loadingRef.current = true;
      if (isLoadMore) {
        setLoadingMore(true);
      } else if (!options?.silent) {
        setLoading(true);
        setError(null);
      }
      try {
        const excludeIds = getSafeExcludeIds(isLoadMore);

        const result = await reelService.getFeed({
          take: FEED_PAGE_SIZE,
          cursorUtc: nextCursorRef.current ?? undefined,
          excludeIds,
        });

        if (!isLoadMore && requestId !== latestRefreshRequestIdRef.current) {
          return null;
        }

        if (!result.isSuccess) {
          if (!options?.silent) {
            setError(result.message ?? 'Reels yüklenemedi.');
            if (!isLoadMore) setReels([]);
            setHasMore(false);
          }
          return null;
        }
        const payload = extractFeedPayload(result);
        nextCursorRef.current = payload.nextCursorUtc;
        setHasMore(
          typeof payload.hasMore === 'boolean'
            ? payload.hasMore
            : !!payload.nextCursorUtc && payload.items.length === FEED_PAGE_SIZE
        );
        payload.items.forEach((item) => {
          if (item.id) addSeenId(item.id);
        });
        if (isLoadMore) {
          setReels((prev) => {
            const existingIds = new Set(prev.map((r) => r.id));
            const newItems = payload.items.filter((r) => !existingIds.has(r.id));
            return [...prev, ...newItems];
          });
        } else if (!options?.silent) {
          setReels(payload.items);
        }
        return payload.items;
      } catch (err) {
        if (!options?.silent) {
          setError(err instanceof Error ? err.message : 'Bir hata oluştu.');
          if (!isLoadMore) setReels([]);
          setHasMore(false);
        }
        return null;
      } finally {
        if (!isLoadMore) {
          if (requestId === latestRefreshRequestIdRef.current) {
            setLoading(false);
          }
        } else {
          setLoadingMore(false);
        }
        loadingRef.current = false;
      }
    },
    [getSafeExcludeIds, addSeenId]
  );

  const prependCreatedReel = useCallback((createdReel: ReelViewModel) => {
    if (!createdReel.id || !createdReel.videoUrl) return;
    setReels((prev) => [createdReel, ...prev.filter((item) => item.id !== createdReel.id)]);
    addSeenId(createdReel.id);
  }, [addSeenId]);

  const removeReel = useCallback((reelId: string) => {
    setReels((prev) => prev.filter((item) => item.id !== reelId));
  }, []);

  const updateReelSaved = useCallback((reelId: string, isSaved: boolean) => {
    setReels((prev) =>
      prev.map((item) => (item.id === reelId ? { ...item, isSaved } : item))
    );
  }, []);

  const refetch = useCallback(async (createdReel?: ReelViewModel) => {
    const hasExisting = reelsRef.current.length > 0;
    nextCursorRef.current = null;
    const items = await fetchPage(false, { silent: hasExisting });

    if (createdReel?.id && createdReel.videoUrl) {
      const mergedReel = mergeCreatedReel(createdReel, items ?? []);
      setReels((prev) => {
        const baseItems = items ?? prev.filter((item) => item.id !== mergedReel.id);
        return [mergedReel, ...baseItems.filter((item) => item.id !== mergedReel.id)];
      });
      addSeenId(mergedReel.id);
      return;
    }

    if (items) {
      setReels(items);
    }
  }, [fetchPage, addSeenId]);

  const loadMore = useCallback(() => {
    if (!hasMore || loading || loadingMore) return;
    fetchPage(true);
  }, [hasMore, loading, loadingMore, fetchPage]);

  useEffect(() => {
    fetchPage(false);
  }, [fetchPage]);

  return {
    reels,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refetch,
    prependCreatedReel,
    removeReel,
    updateReelSaved,
    addSeenId,
  };
}
